import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Psychology as AgentIcon,
  Save as SaveIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import {
  assistantAgentAPI,
  type VysorAgentConfigPublic,
  type VysorAgentProvider,
  type VysorCapabilityToggle,
  type VysorProviderCatalogItem,
  type VysorRuntimeMode,
} from '../services/api';
import { PAGE } from '../constants/pageStyles';

const CARD_SX = { border: '1px solid', borderColor: 'divider', boxShadow: 'none' } as const;
const CC_SX = { p: 2, '&:last-child': { pb: 2 } } as const;
const MAX_TOKENS_MIN = 256;
const MAX_TOKENS_MAX = 16384;
const MAX_TOKENS_DEFAULT = 2500;

function clampMaxTokens(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return MAX_TOKENS_DEFAULT;
  return Math.min(MAX_TOKENS_MAX, Math.max(MAX_TOKENS_MIN, Math.floor(n)));
}

const DEFAULT_PROVIDER_CATALOG: VysorProviderCatalogItem[] = [
  {
    id: 'azure_foundry',
    label: 'Azure AI Foundry',
    description: 'Microsoft Foundry — chat completions and optional Foundry Agent Service.',
  },
  {
    id: 'azure_openai',
    label: 'Azure OpenAI',
    description: 'Classic Azure OpenAI / Cognitive Services chat deployments.',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    description: 'OpenAI platform (api.openai.com) with your API key and model name.',
  },
  {
    id: 'openai_compatible',
    label: 'OpenAI-compatible API',
    description: 'Any OpenAI Chat Completions–compatible service (custom gateway, Groq, local, etc.).',
  },
];

type AgentConfigForm = {
  agent_name: string;
  provider: VysorAgentProvider;
  resource_endpoint: string;
  foundry_project: string;
  foundry_agent_id: string;
  runtime_mode: VysorRuntimeMode;
  deployment_name: string;
  api_version: string;
  temperature: number;
  /** Empty string only while the user is clearing the field to retype. */
  max_tokens: number | '';
  is_active: boolean;
  api_key_configured: boolean;
  api_key_masked?: string;
  capabilities: Record<string, boolean>;
  capability_catalog: VysorCapabilityToggle[];
  provider_catalog: VysorProviderCatalogItem[];
};

const emptyForm = (): AgentConfigForm => ({
  agent_name: 'VYSOR agent',
  provider: 'azure_foundry',
  resource_endpoint: 'https://provysn-test-agent.services.ai.azure.com/',
  foundry_project: 'proj-default',
  foundry_agent_id: '',
  runtime_mode: 'auto',
  deployment_name: 'gpt-4o',
  api_version: '2024-11-20',
  temperature: 0.3,
  max_tokens: 2500,
  is_active: true,
  api_key_configured: false,
  capabilities: {},
  capability_catalog: [],
  provider_catalog: DEFAULT_PROVIDER_CATALOG,
});

function formFromConfig(cfg: VysorAgentConfigPublic): AgentConfigForm {
  return {
    agent_name: cfg.agent_name,
    provider: cfg.provider || 'azure_foundry',
    resource_endpoint: cfg.resource_endpoint,
    foundry_project: cfg.foundry_project || 'proj-default',
    foundry_agent_id: cfg.foundry_agent_id || '',
    runtime_mode: cfg.runtime_mode || 'auto',
    deployment_name: cfg.deployment_name,
    api_version: cfg.api_version,
    temperature: cfg.temperature,
    max_tokens: Number(cfg.max_tokens) > 0 ? Number(cfg.max_tokens) : 2500,
    is_active: cfg.is_active,
    api_key_configured: cfg.api_key_configured,
    api_key_masked: cfg.api_key_masked,
    capabilities: { ...(cfg.capabilities || {}) },
    capability_catalog: cfg.capability_catalog || [],
    provider_catalog: cfg.provider_catalog?.length ? cfg.provider_catalog : DEFAULT_PROVIDER_CATALOG,
  };
}

const AgentConfigurationSettings: React.FC = () => {
  const [form, setForm] = useState<AgentConfigForm>(emptyForm);
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const isAzureFoundry = form.provider === 'azure_foundry';
  const isAzure = form.provider === 'azure_foundry' || form.provider === 'azure_openai';
  const isOpenAiCompatible = form.provider === 'openai' || form.provider === 'openai_compatible';

  const providerMeta = useMemo(
    () => form.provider_catalog.find(p => p.id === form.provider) || DEFAULT_PROVIDER_CATALOG[0],
    [form.provider, form.provider_catalog]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const cfg = await assistantAgentAPI.getConfig();
      setForm(formFromConfig(cfg));
      setApiKey('');
      setClearApiKey(false);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to load agent configuration.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const connectionPayload = () => {
    const maxTokens = clampMaxTokens(form.max_tokens);
    const payload: Record<string, unknown> = {
      agent_name: form.agent_name.trim(),
      provider: form.provider,
      resource_endpoint: form.resource_endpoint.trim(),
      foundry_project: form.foundry_project.trim(),
      foundry_agent_id: form.foundry_agent_id.trim(),
      runtime_mode: form.runtime_mode,
      deployment_name: form.deployment_name.trim(),
      api_version: form.api_version.trim(),
      temperature: form.temperature,
      max_tokens: maxTokens,
      is_active: form.is_active,
      capabilities: form.capabilities,
    };
    if (apiKey.trim()) payload.api_key = apiKey.trim();
    if (clearApiKey) payload.clear_api_key = true;
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await assistantAgentAPI.saveConfig(connectionPayload());
      setForm(formFromConfig(saved));
      setApiKey('');
      setClearApiKey(false);
      setMessage({ type: 'success', text: 'Agent configuration saved. VYSOR will use these settings immediately.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to save agent configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      await assistantAgentAPI.saveConfig(connectionPayload());
      const result = await assistantAgentAPI.testConnection();
      setMessage({
        type: result.connected ? 'success' : 'error',
        text: result.message,
      });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleProvisionFoundry = async () => {
    setProvisioning(true);
    setMessage(null);
    try {
      await assistantAgentAPI.saveConfig(connectionPayload());
      const result = await assistantAgentAPI.provisionFoundryAgent();
      setForm(formFromConfig(result.config));
      setMessage({
        type: 'success',
        text: result.created
          ? `Created Foundry agent (${result.agent_id}). Runtime will use Foundry when agent id is set.`
          : `Updated Foundry agent (${result.agent_id}).`,
      });
    } catch (err: any) {
      const detail =
        err?.response?.data?.details ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to provision Foundry agent.';
      setMessage({ type: 'error', text: String(detail) });
    } finally {
      setProvisioning(false);
    }
  };

  const toggleCapability = (id: string, enabled: boolean) => {
    setForm(prev => ({
      ...prev,
      capabilities: { ...prev.capabilities, [id]: enabled },
      capability_catalog: prev.capability_catalog.map(c =>
        c.id === id ? { ...c, enabled } : c
      ),
    }));
  };

  const onProviderChange = (provider: VysorAgentProvider) => {
    setForm(prev => {
      const next = { ...prev, provider };
      if (provider === 'openai' && !prev.resource_endpoint.includes('openai.com')) {
        next.resource_endpoint = 'https://api.openai.com/v1';
        next.api_version = '';
        next.runtime_mode = 'chat_completions';
      }
      if (provider === 'openai_compatible') {
        next.runtime_mode = 'chat_completions';
      }
      if (provider === 'azure_openai' && prev.runtime_mode === 'foundry') {
        next.runtime_mode = 'chat_completions';
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <AgentIcon color="primary" sx={{ fontSize: 22 }} />
          <Typography sx={PAGE.subsectionTitle}>Configure Agent (VYSOR)</Typography>
        </Stack>
        <Typography sx={PAGE.helper}>
          Connect VYSOR to <strong>your</strong> LLM / agent backend — Azure AI Foundry, Azure OpenAI, OpenAI, or any
          OpenAI-compatible API. Policy instructions are managed by Provysn; use <strong>Capabilities</strong> to enable
          behavior packs.
        </Typography>
        {isAzure && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            For Azure: create a <strong>model deployment</strong> in the portal and use the exact deployment name below.
          </Alert>
        )}
      </Box>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card variant="outlined" sx={CARD_SX}>
        <CardContent sx={CC_SX}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 2 }}>Provider & connection</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="vysor-provider-label">Provider</InputLabel>
                <Select
                  labelId="vysor-provider-label"
                  label="Provider"
                  value={form.provider}
                  onChange={e => onProviderChange(e.target.value as VysorAgentProvider)}
                >
                  {(form.provider_catalog.length ? form.provider_catalog : DEFAULT_PROVIDER_CATALOG).map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {providerMeta.description}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display name"
                value={form.agent_name}
                onChange={e => setForm(prev => ({ ...prev, agent_name: e.target.value }))}
                helperText="Label for this enterprise connection"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={isOpenAiCompatible ? 'Model name' : 'Deployment name'}
                value={form.deployment_name}
                onChange={e => setForm(prev => ({ ...prev, deployment_name: e.target.value }))}
                helperText={
                  isOpenAiCompatible
                    ? 'e.g. gpt-4o, gpt-4.1-mini — exact model id from your provider'
                    : 'Exact deployment name from Azure portal → Model deployments'
                }
              />
            </Grid>
            {isAzureFoundry && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="vysor-runtime-mode-label">Runtime mode</InputLabel>
                  <Select
                    labelId="vysor-runtime-mode-label"
                    label="Runtime mode"
                    value={form.runtime_mode}
                    onChange={e => setForm(prev => ({ ...prev, runtime_mode: e.target.value as VysorRuntimeMode }))}
                  >
                    <MenuItem value="auto">Auto (Foundry Agents when agent id is set)</MenuItem>
                    <MenuItem value="foundry">Foundry Agent Service only</MenuItem>
                    <MenuItem value="chat_completions">Chat completions only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {isAzureFoundry && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Foundry project"
                  value={form.foundry_project}
                  onChange={e => setForm(prev => ({ ...prev, foundry_project: e.target.value }))}
                  helperText="Project name in Azure AI Foundry (e.g. proj-default)"
                />
              </Grid>
            )}
            {isAzureFoundry && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Foundry agent id"
                  value={form.foundry_agent_id}
                  onChange={e => setForm(prev => ({ ...prev, foundry_agent_id: e.target.value }))}
                  placeholder="asst_…"
                  helperText="Optional. Paste from Azure portal or use Provision agent. Required only for Foundry Agent Service runtime."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isOpenAiCompatible ? 'API base URL' : 'Resource endpoint'}
                value={form.resource_endpoint}
                onChange={e => setForm(prev => ({ ...prev, resource_endpoint: e.target.value }))}
                helperText={
                  form.provider === 'openai'
                    ? 'Usually https://api.openai.com/v1'
                    : form.provider === 'openai_compatible'
                      ? 'Your provider base URL (often ends with /v1)'
                      : 'e.g. https://your-resource.services.ai.azure.com/'
                }
              />
            </Grid>
            {isAzure && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API version"
                  value={form.api_version}
                  onChange={e => setForm(prev => ({ ...prev, api_version: e.target.value }))}
                  helperText="Azure API version (e.g. 2024-11-20)"
                />
              </Grid>
            )}
            <Grid item xs={12} md={isAzure ? 6 : 12}>
              <TextField
                fullWidth
                type="password"
                label="API key"
                value={apiKey}
                onChange={e => {
                  setApiKey(e.target.value);
                  setClearApiKey(false);
                }}
                placeholder={
                  form.api_key_configured
                    ? form.api_key_masked || '••••••••'
                    : 'Paste API key from your provider'
                }
                helperText={
                  form.api_key_configured && !apiKey
                    ? 'Leave blank to keep the stored key'
                    : 'Required before VYSOR chat works. Stored encrypted.'
                }
              />
            </Grid>
            {form.api_key_configured && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={clearApiKey}
                      onChange={e => setClearApiKey(e.target.checked)}
                    />
                  }
                  label="Clear stored API key"
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={CARD_SX}>
        <CardContent sx={CC_SX}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 1 }}>Agent policy</Typography>
          <Alert severity="success" sx={{ mb: 0 }}>
            Agent policy instructions (AGENTS.md) are managed by Provysn and are not editable here.
            They are applied server-side on every chat request.
          </Alert>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={CARD_SX}>
        <CardContent sx={CC_SX}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 1 }}>Capabilities (plugins)</Typography>
          <Typography sx={{ ...PAGE.helper, mb: 2 }}>
            Toggle behavior packs that train VYSOR for this enterprise. Each pack loads server-owned prompt modules
            and related tools or deterministic flows. Defaults match current production behavior.
          </Typography>
          <Stack spacing={1.5}>
            {(form.capability_catalog.length
              ? form.capability_catalog
              : Object.keys(form.capabilities).map(id => ({
                  id,
                  label: id,
                  description: '',
                  enabled: form.capabilities[id] !== false,
                }))
            ).map(cap => (
              <Box
                key={cap.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2">{cap.label}</Typography>
                  {cap.description ? (
                    <Typography variant="body2" color="text.secondary">
                      {cap.description}
                    </Typography>
                  ) : null}
                </Box>
                <Switch
                  checked={form.capabilities[cap.id] !== false}
                  onChange={e => toggleCapability(cap.id, e.target.checked)}
                  inputProps={{ 'aria-label': `Toggle ${cap.label}` }}
                />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={CARD_SX}>
        <CardContent sx={CC_SX}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 2 }}>Generation settings</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Temperature: {form.temperature.toFixed(2)}
              </Typography>
              <Slider
                value={form.temperature}
                min={0}
                max={1}
                step={0.05}
                onChange={(_, v) => setForm(prev => ({ ...prev, temperature: v as number }))}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max tokens"
                value={form.max_tokens}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setForm(prev => ({ ...prev, max_tokens: '' }));
                    return;
                  }
                  const n = Number(raw);
                  if (!Number.isFinite(n)) return;
                  setForm(prev => ({ ...prev, max_tokens: Math.floor(n) }));
                }}
                onBlur={() => {
                  setForm(prev => ({ ...prev, max_tokens: clampMaxTokens(prev.max_tokens) }));
                }}
                helperText="256–16384. 16000 is allowed. Used on the next chat turn."
                inputProps={{ min: MAX_TOKENS_MIN, max: MAX_TOKENS_MAX, step: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Agent active for this enterprise"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || testing || provisioning}
        >
          {saving ? 'Saving…' : 'Save configuration'}
        </Button>
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
          onClick={handleTest}
          disabled={saving || testing || provisioning}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </Button>
        {isAzureFoundry && (
          <Button
            variant="outlined"
            onClick={handleProvisionFoundry}
            disabled={saving || testing || provisioning}
          >
            {provisioning ? 'Provisioning…' : 'Provision Foundry agent'}
          </Button>
        )}
        <Button variant="text" onClick={loadConfig} disabled={saving || testing || provisioning}>
          Reload
        </Button>
      </Stack>
    </Stack>
  );
};

export default AgentConfigurationSettings;
